<template>
  <div>
    <div class="app-content pt-3 p-md-3 p-lg-4">
      <div class="container-fluid">
        <h1 class="app-page-title">Overview</h1>

        <div
          class="app-card alert alert-dismissible shadow-sm mb-4 border-left-decoration"
          role="alert"
        >
          <div class="inner">
            <div class="app-card-body p-3 p-lg-4">
              <h3 class="mb-3">Welcome, {{ currentUserName }}!</h3>
              <div class="row gx-5 gy-3">
                <div class="col-12 col-lg-9">
                  <div>
                    The ultimate goal of a university is the acquisition,
                    conservation, dissemination and application of knowledge.
                    What distinguishes a university from another is the way the
                    goal is pursued.
                  </div>
                </div>
                <div class="col-12 col-lg-3">
                  <a class="btn app-btn-primary" href="https://run.edu.ng/">
                    <svg
                      width="1em"
                      height="1em"
                      viewBox="0 0 16 16"
                      class="bi bi-file-earmark-arrow-down mr-2"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 0h5.5v1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h1V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"
                      />
                      <path d="M9.5 3V0L14 4.5h-3A1.5 1.5 0 0 1 9.5 3z" />
                      <path
                        fill-rule="evenodd"
                        d="M8 6a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 1 1 .708-.708L7.5 10.293V6.5A.5.5 0 0 1 8 6z"
                      />
                    </svg>
                    Download Report(s)
                  </a>
                </div>
              </div>
              <button
                type="button"
                class="btn-close"
                data-dismiss="alert"
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center my-5 py-5">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
          <div class="mt-3 text-muted"><strong>Loading dashboard data...</strong></div>
        </div>

        <template v-else>
          <!-- Application Stats - visible to all admin roles -->
          <div class="row g-4 mb-4" v-if="showAdmissionStats">
            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Total Applicants</h4>
                  <div class="stats-figure">{{ formatNumber(stats.total) }}</div>
                  <div class="stats-meta text-muted">Current session</div>
                </div>
              </div>
            </div>

            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Admitted</h4>
                  <div class="stats-figure">{{ formatNumber(stats.admitted) }}</div>
                  <div class="stats-meta text-success">
                    {{ admittedPercent }}% of applicants
                  </div>
                </div>
              </div>
            </div>

            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Pending</h4>
                  <div class="stats-figure">{{ formatNumber(stats.pending) }}</div>
                  <div class="stats-meta text-warning">Under processing</div>
                </div>
              </div>
            </div>

            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Recommended</h4>
                  <div class="stats-figure">{{ formatNumber(stats.recommended) }}</div>
                  <div class="stats-meta text-info">For admission</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional admission stats row - visible to all admin roles -->
          <div class="row g-4 mb-4" v-if="showAdmissionStats">
            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Denied</h4>
                  <div class="stats-figure">{{ formatNumber(stats.denied) }}</div>
                  <div class="stats-meta text-danger">Denied admission</div>
                </div>
              </div>
            </div>

            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Awaiting Submission</h4>
                  <div class="stats-figure">{{ formatNumber(stats.awaiting) }}</div>
                  <div class="stats-meta text-muted">Incomplete forms</div>
                </div>
              </div>
            </div>

            <div class="col-6 col-lg-3">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Total Students</h4>
                  <div class="stats-figure">{{ formatNumber(studentCount) }}</div>
                  <div class="stats-meta text-info">Registered students</div>
                </div>
              </div>
            </div>

            <div class="col-6 col-lg-3" v-if="showPaymentStats">
              <div class="app-card app-card-stat shadow-sm h-100">
                <div class="app-card-body p-3 p-lg-4">
                  <h4 class="stats-type mb-1">Total Revenue</h4>
                  <div class="stats-figure">{{ formatCurrency(totalRevenue) }}</div>
                  <div class="stats-meta text-success">
                    {{ paymentSummary.session || 'Current session' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Stats - SUPERADMIN and BURSARY only -->
          <div class="row g-4 mb-4" v-if="showPaymentStats && paymentSummary">
            <div class="col-12">
              <div class="app-card shadow-sm">
                <div class="app-card-header p-3">
                  <h4 class="app-card-title m-0">Payment Overview ({{ paymentSummary.session || 'Current Session' }})</h4>
                </div>
                <div class="app-card-body p-3">
                  <div class="row">
                    <div class="col-md-3">
                      <div class="text-center p-3">
                        <div class="text-muted mb-1">Applicant Collections</div>
                        <h4 class="text-success mb-0">{{ formatCurrency(paymentSummary.applicant_collected) }}</h4>
                        <small class="text-muted">
                          {{ formatNumber(paymentSummary.applicant_success) }} successful |
                          {{ formatNumber(paymentSummary.applicant_pending) }} pending
                        </small>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="text-center p-3">
                        <div class="text-muted mb-1">Student Collections</div>
                        <h4 class="text-success mb-0">{{ formatCurrency(paymentSummary.student_collected) }}</h4>
                        <small class="text-muted">
                          {{ formatNumber(paymentSummary.student_success) }} successful |
                          {{ formatNumber(paymentSummary.student_pending) }} pending
                        </small>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="text-center p-3">
                        <div class="text-muted mb-1">Total Successful</div>
                        <h4 class="text-info mb-0">
                          {{ formatNumber(Number(paymentSummary.applicant_success || 0) + Number(paymentSummary.student_success || 0)) }}
                        </h4>
                        <small class="text-muted">transactions</small>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="text-center p-3">
                        <div class="text-muted mb-1">Total Pending</div>
                        <h4 class="text-warning mb-0">
                          {{ formatNumber(Number(paymentSummary.applicant_pending || 0) + Number(paymentSummary.student_pending || 0)) }}
                        </h4>
                        <small class="text-muted">transactions</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Links -->
          <div class="row g-4 mb-4">
            <div class="col-12 col-lg-4" v-if="showAdmissionStats">
              <div
                class="app-card app-card-basic d-flex flex-column align-items-start shadow-sm"
              >
                <div class="app-card-header p-3 border-bottom-0">
                  <div class="row align-items-center gx-3">
                    <div class="col-auto">
                      <div class="app-icon-holder">
                        <b-icon icon="people-fill"></b-icon>
                      </div>
                    </div>
                    <div class="col-auto">
                      <h4 class="app-card-title">Applicants</h4>
                    </div>
                  </div>
                </div>
                <div class="app-card-body px-4">
                  <div class="intro">
                    View and manage all applicant records, admission status, and application forms.
                  </div>
                </div>
                <div class="app-card-footer p-4 mt-auto">
                  <router-link class="btn app-btn-secondary" :to="{ name: 'applicants' }">
                    View Applicants
                  </router-link>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4" v-if="showPaymentStats">
              <div
                class="app-card app-card-basic d-flex flex-column align-items-start shadow-sm"
              >
                <div class="app-card-header p-3 border-bottom-0">
                  <div class="row align-items-center gx-3">
                    <div class="col-auto">
                      <div class="app-icon-holder">
                        <b-icon icon="cash-stack"></b-icon>
                      </div>
                    </div>
                    <div class="col-auto">
                      <h4 class="app-card-title">Bursary</h4>
                    </div>
                  </div>
                </div>
                <div class="app-card-body px-4">
                  <div class="intro">
                    Manage billing, view student ledger, and download gateway-specific reports.
                  </div>
                </div>
                <div class="app-card-footer p-4 mt-auto">
                  <router-link class="btn app-btn-secondary" :to="{ name: 'bursaryDashboard' }">
                    Bursary Dashboard
                  </router-link>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4" v-if="showAdmissionStats">
              <div
                class="app-card app-card-basic d-flex flex-column align-items-start shadow-sm"
              >
                <div class="app-card-header p-3 border-bottom-0">
                  <div class="row align-items-center gx-3">
                    <div class="col-auto">
                      <div class="app-icon-holder">
                        <b-icon icon="mortarboard-fill"></b-icon>
                      </div>
                    </div>
                    <div class="col-auto">
                      <h4 class="app-card-title">Students</h4>
                    </div>
                  </div>
                </div>
                <div class="app-card-body px-4">
                  <div class="intro">
                    Manage student profiles, matric number assignment, and academic records.
                  </div>
                </div>
                <div class="app-card-footer p-4 mt-auto">
                  <router-link class="btn app-btn-secondary" :to="{ name: 'studprofile' }">
                    Student Profiles
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      stats: {
        total: 0,
        admitted: 0,
        pending: 0,
        recommended: 0,
        denied: 0,
        awaiting: 0,
      },
      studentCount: 0,
      paymentSummary: null,
      currentUser: this.$store.getters["getUser"],
    };
  },
  computed: {
    currentUserName() {
      return this.currentUser?.user?.surname || "Guest";
    },
    userRoles() {
      return this.$store.state.user.roles || [];
    },
    isSuperAdmin() {
      return this.userRoles.includes("SUPERADMIN");
    },
    showAdmissionStats() {
      return (
        this.isSuperAdmin ||
        this.userRoles.some((r) =>
          ["ADMIN", "ICT", "HOD", "ADMISSIONOFFICER", "PGSCHOOL", "COLLEGEOFFICER", "PG-COORD"].includes(r)
        )
      );
    },
    showPaymentStats() {
      return this.isSuperAdmin || this.userRoles.includes("BURSARY");
    },
    admittedPercent() {
      if (!this.stats.total) return 0;
      return ((this.stats.admitted / this.stats.total) * 100).toFixed(1);
    },
    totalRevenue() {
      if (!this.paymentSummary) return 0;
      return (
        Number(this.paymentSummary.applicant_collected || 0) +
        Number(this.paymentSummary.student_collected || 0)
      );
    },
  },
  created() {
    this.loadOverviewStats();
  },
  methods: {
    formatNumber(val) {
      return Number(val || 0).toLocaleString("en-NG");
    },
    formatCurrency(val) {
      return (
        "₦" +
        Number(val || 0).toLocaleString("en-NG", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    loadOverviewStats() {
      this.loading = true;
      let params = {};
      if (this.$store.state.user.deptName) {
        params.dept_name = this.$store.state.user.deptName;
      }
      axios
        .get("admin/overview-stats", { params })
        .then((response) => {
          if (response.data.msg === "success") {
            this.stats = response.data.applications || this.stats;
            this.studentCount = response.data.student_count || 0;
            this.paymentSummary = response.data.payment_summary || null;
          }
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load dashboard stats" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.stats-figure {
  font-size: 1.8rem;
  font-weight: 700;
  color: #252930;
}
.stats-type {
  font-size: 0.875rem;
  color: #6c757d;
}
</style>
